<script setup lang="ts">
import { useMusicPlayerStoreHook } from '@/store/modules/musicPlayer'
import { ref, onMounted } from 'vue'

const musicPlayerStore = useMusicPlayerStoreHook()
const showAnimation = ref<boolean>(false)
const isShowBtn = ref<boolean>(false)
const isDragging = ref<boolean>(false)
const hasDragged = ref<boolean>(false)
const dragStartTime = ref<number>(0)
const dragDistance = ref<number>(0)
const position = ref({ x: window.innerWidth - 74, y: window.innerHeight * 0.6 })
const dragOffset = ref({ x: 0, y: 0 })
const currentSong = computed(() => {
  if (musicPlayerStore.currentSongIndex !== null && musicPlayerStore.playlist.length > 0) {
    return musicPlayerStore.playlist[musicPlayerStore.currentSongIndex]
  }
  return null
})

const handleMouseDown = (e: MouseEvent) => {
  if (
    (e.target as HTMLElement).closest('.control-btn') ||
    (e.target as HTMLElement).closest('img') ||
    (e.target as HTMLElement).closest('.avatar')
  ) {
    return
  }
  isDragging.value = true
  hasDragged.value = false
  dragDistance.value = 0
  dragStartTime.value = Date.now()
  dragOffset.value = {
    x: e.clientX - position.value.x,
    y: e.clientY - position.value.y,
  }
}

const handleMouseMove = (e: MouseEvent) => {
  if (isDragging.value) {
    const newX = e.clientX - dragOffset.value.x
    const newY = e.clientY - dragOffset.value.y

    // 计算拖拽距离
    const dx = newX - position.value.x
    const dy = newY - position.value.y
    dragDistance.value += Math.sqrt(dx * dx + dy * dy)

    // 判断是否真的拖拽了
    if (dragDistance.value > 5) {
      hasDragged.value = true
    }

    // 限制在窗口内并直接设置位置，不使用过渡动画
    position.value = {
      x: Math.max(0, Math.min(newX, window.innerWidth - 74)),
      y: Math.max(0, Math.min(newY, window.innerHeight - 78)),
    }
  }
}

const handleMouseUp = (e: MouseEvent) => {
  if (isDragging.value) {
    const dragDuration = Date.now() - dragStartTime.value

    isDragging.value = false

    // 如果拖拽距离小且时间短，认为是点击
    if (dragDistance.value < 5 && dragDuration < 200) {
      hasDragged.value = false
      return
    }

    // 判断最终停靠位置
    const isLeftSide = position.value.x < window.innerWidth / 2

    // 平滑动画到停靠位置，保持当前的垂直位置
    position.value = {
      x: isLeftSide ? 0 : window.innerWidth - 74,
      y: position.value.y,
    }
  }
}

onMounted(() => {
  setTimeout(() => {
    showAnimation.value = true
  }, 300)

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
})

const handleFullscreen = () => {
  // 只有在非拖拽状态下才响应点击
  if (hasDragged.value) {
    return
  }
  musicPlayerStore.toggleFullscreen()
}

const handleClose = () => {
  musicPlayerStore.togglePlay()
  musicPlayerStore.setCurrentSongIndex(null)
}

const isShow = computed(() => {
  if (!musicPlayerStore.isFullscreen && musicPlayerStore.isPlaying && !musicPlayerStore.isVisible) {
    musicPlayerStore.closeDrawerList()
  }
  return !musicPlayerStore.isFullscreen && musicPlayerStore.isPlaying && !musicPlayerStore.isVisible
})
</script>

<template>
  <div
    v-if="isShow"
    class="fixed rounded-[14px] flex justify-center items-center w-[74px] h-[78px] little-btn z-[998]"
    :class="{
      'throw-in-animation': showAnimation,
      'dock-animation': !isDragging && hasDragged,
    }"
    :style="{
      left: `${position.x}px`,
      top: `${position.y}px`,
      cursor: isDragging ? 'grabbing' : 'grab',
      transition: isDragging ? 'none' : undefined,
    }"
    @mousedown="handleMouseDown"
    @mouseenter="isShowBtn = true"
    @mouseleave="isShowBtn = false"
    @click="handleFullscreen"
    @dragstart.prevent
  >
    <img
      src="@/assets/space/record.png"
      class="w-[auto] h-[54px] record-img no-drag"
      draggable="false"
    />
    <div class="absolute top-0 left-0 w-full h-full flex justify-center items-center">
      <img
        :src="currentSong?.thumbnailUrl || currentSong?.imageUrl"
        class="avatar w-[24px] h-[24px] rounded-[50%] overflow-hidden no-drag"
        :class="{
          rotating: musicPlayerStore.isPlaying,
          paused: !musicPlayerStore.isPlaying,
        }"
        draggable="false"
      />
    </div>
    <el-tooltip content="全屏" placement="top" effect="dark" v-if="isShowBtn">
      <img
        src="@/assets/space/full-icon.png"
        class="absolute top-[6px] left-[4px] w-[12px] h-[12px] hover-transform control-btn no-drag"
        draggable="false"
      />
    </el-tooltip>
    <el-tooltip content="关闭" placement="top" effect="dark" v-if="isShowBtn">
      <img
        src="@/assets/space/close.png"
        class="absolute top-[4px] right-[6px] z-[99999] w-[12px] h-[12px] hover-transform !hover:scale-125 control-btn no-drag"
        @click.stop="handleClose"
        draggable="false"
      />
    </el-tooltip>
  </div>
</template>
<style scoped>
.rotating {
  animation: rotating 20s linear infinite;
}

.little-btn {
  background: linear-gradient(136deg, #b6dfff 0%, #f2f7fb 77%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  opacity: 0;
  transform: translateX(100px) scale(0.5);
  user-select: none;
  touch-action: none;
  will-change: transform, left, top;
}

.throw-in-animation {
  animation: throwIn 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}

.dock-animation {
  transition: left 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes throwIn {
  0% {
    opacity: 0;
    transform: translateX(100px) scale(0.5) rotate(-45deg);
  }

  70% {
    opacity: 1;
    transform: translateX(-10px) scale(1.1) rotate(5deg);
  }

  85% {
    transform: translateX(5px) scale(0.95) rotate(-2deg);
  }

  100% {
    opacity: 1;
    transform: translateX(0) scale(1) rotate(0deg);
  }
}

.record-img {
  transition: transform 0.3s ease;
  animation-delay: 0.2s;
}

.little-btn:hover {
  transform: scale(1.1);
  box-shadow: -3px 3px 12px rgba(0, 0, 0, 0.2);
}

.little-btn:hover img.record-img {
  transform: scale(1.05);
}

.little-btn img {
  transition: transform 0.3s ease;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.hover-transform {
  cursor: pointer;
  transition: all 0.3s ease;
}

.hover-transform:hover {
  transform: scale(1.2);
}

.no-drag {
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
}
</style>
